import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import AdminLayout from "@/components/admin/AdminLayout"
import { getAdminOrders } from "@/api/adminOrderApi"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Plus } from "lucide-react"

type AdminOrder = {
    orderId: number
    orderNo: string
    ordererName: string
    totalAmount: number
    paymentMethod: string
    orderStatus: string
    createdAt: string
}

// 💡 한글 변환 헬퍼 함수
const formatPrice = (value: number) => `${value.toLocaleString("ko-KR")}원`
const getStatusText = (status: string) => {
    const map: Record<string, string> = { CREATED: "주문생성", PAID: "결제완료", PREPARING: "배송준비중", SHIPPING: "배송중", DONE: "배송완료", CANCELED: "주문취소" }
    return map[status] || status
}
const getPaymentText = (method: string) => {
    const map: Record<string, string> = { CARD: "카드결제", BANK: "무통장입금", KAKAOPAY: "카카오페이" }
    return map[method] || method
}
const getStatusColor = (status: string) => {
    if (status === 'PAID') return 'bg-blue-100 text-blue-700'
    if (status === 'PREPARING' || status === 'SHIPPING') return 'bg-amber-100 text-amber-700'
    if (status === 'DONE') return 'bg-green-100 text-green-700'
    if (status === 'CANCELED') return 'bg-rose-100 text-rose-700'
    return 'bg-slate-100 text-slate-700'
}

export default function AdminOrderPage() {
    const navigate = useNavigate()

    const [orders, setOrders] = useState<AdminOrder[]>([])
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState({ searchKeyword: "", orderStatus: "", paymentMethod: "" })

    // 💡 1. 페이징 관련 상태 추가
    const [page, setPage] = useState(0) // 현재 페이지 (0부터 시작)
    const [totalPages, setTotalPages] = useState(0) // 전체 페이지 수
    const [totalElements, setTotalElements] = useState(0) // 전체 주문 건수
    const pageSize = 10 // 한 페이지당 표시할 개수 (필요시 20으로 수정 가능)

    // 💡 2. 목록 조회 함수 수정 (targetPage 인자 추가 및 응답 데이터 매핑)
    const fetchOrders = async (targetPage = page) => {
        try {
            setLoading(true)
            const response = await getAdminOrders({
                searchKeyword: search.searchKeyword || undefined,
                orderStatus: search.orderStatus || undefined,
                paymentMethod: search.paymentMethod || undefined,
                page: targetPage,
                size: pageSize,
            })

            const responseData = response.data || response

            setOrders(responseData.items || [])
            setTotalPages(responseData.totalPages || 0)
            setTotalElements(responseData.totalElements || 0)
            setPage(targetPage)
        } catch (error) {
            console.error(error)
            alert("주문 목록을 불러오지 못했습니다.")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchOrders(0)
    }, [])

    // 💡 3. 검색 시 1페이지(0번 인덱스)부터 조회하도록 연동
    const handleSearch = () => {
        fetchOrders(0)
    }

    // 💡 4. 페이지 이동 핸들러
    const handlePageChange = (newPage: number) => {
        if (newPage < 0 || newPage >= totalPages) return
        fetchOrders(newPage)
    }

    return (
      <AdminLayout>
          <div className="space-y-6">
              <div className="flex items-end justify-between">
                  <div>
                      <h2 className="text-3xl font-bold">주문관리</h2>
                      <p className="mt-2 text-sm text-slate-500">주문 상태와 결제 정보를 관리합니다.</p>
                  </div>
                  <Button
                    className="rounded-xl bg-slate-900 gap-2 h-11 px-5"
                    onClick={() => navigate('/admin/orders/create')}
                  >
                      <Plus size={18} />
                      수기 주문서 작성
                  </Button>
              </div>

              <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                  <CardHeader>
                      <CardTitle className="text-xl">주문 검색</CardTitle>
                  </CardHeader>
                  <CardContent>
                      <div className="grid gap-3 md:grid-cols-[1fr_180px_180px_120px]">
                          {/* 💡 검색어 입력 후 Enter 키 입력 시 검색되도록 onKeyDown 이벤트 추가 */}
                          <Input
                            placeholder="주문번호 / 고객명 검색"
                            className="rounded-xl"
                            value={search.searchKeyword}
                            onChange={(e) => setSearch(prev => ({ ...prev, searchKeyword: e.target.value }))}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                          />
                          <select
                            className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none"
                            value={search.orderStatus}
                            onChange={(e) => setSearch(prev => ({ ...prev, orderStatus: e.target.value }))}
                          >
                              <option value="">전체 상태</option>
                              <option value="PAID">결제완료</option>
                              <option value="PREPARING">배송준비중</option>
                              <option value="SHIPPING">배송중</option>
                              <option value="DONE">배송완료</option>
                              <option value="CANCELED">주문취소</option>
                          </select>
                          <select
                            className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none"
                            value={search.paymentMethod}
                            onChange={(e) => setSearch(prev => ({ ...prev, paymentMethod: e.target.value }))}
                          >
                              <option value="">전체 결제수단</option>
                              <option value="CARD">카드결제</option>
                              <option value="BANK">무통장입금</option>
                          </select>
                          <Button className="rounded-xl" onClick={handleSearch}>검색</Button>
                      </div>
                  </CardContent>
              </Card>

              <Card className="rounded-[28px] border border-slate-200 shadow-sm overflow-hidden">
                  <CardContent className="p-0">
                      <div className="overflow-x-auto">
                          <table className="w-full min-w-[920px] text-sm text-left">
                              <thead>
                              <tr className="border-b border-slate-200 bg-slate-50 text-slate-500">
                                  <th className="px-4 py-4 font-medium">주문일시</th>
                                  <th className="px-4 py-4 font-medium">주문번호</th>
                                  <th className="px-4 py-4 font-medium">고객명</th>
                                  <th className="px-4 py-4 font-medium">결제금액</th>
                                  <th className="px-4 py-4 font-medium">결제수단</th>
                                  <th className="px-4 py-4 font-medium">주문상태</th>
                              </tr>
                              </thead>
                              <tbody>
                              {loading ? (
                                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">조회 중...</td></tr>
                              ) : orders.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">주문 내역이 없습니다.</td></tr>
                              ) : (
                                orders.map((order) => (
                                  <tr
                                    key={order.orderId}
                                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                                    onClick={() => navigate(`/admin/orders/${order.orderId}`)}
                                  >
                                      <td className="px-4 py-4 text-slate-500">{order.createdAt}</td>
                                      <td className="px-4 py-4 font-medium text-slate-900">{order.orderNo}</td>
                                      <td className="px-4 py-4">{order.ordererName}</td>
                                      <td className="px-4 py-4 font-medium">{formatPrice(order.totalAmount)}</td>
                                      <td className="px-4 py-4">{getPaymentText(order.paymentMethod)}</td>
                                      <td className="px-4 py-4">
                                                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${getStatusColor(order.orderStatus)}`}>
                                                        {getStatusText(order.orderStatus)}
                                                    </span>
                                      </td>
                                  </tr>
                                ))
                              )}
                              </tbody>
                          </table>
                      </div>

                      {/* 💡 5. 페이징 네비게이션 UI 추가 */}
                      {totalPages > 0 && (
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-100 bg-white">
                            <div className="text-sm text-slate-500">
                                총 <span className="font-semibold text-slate-900">{totalElements}</span>건의 주문 (페이지 {page + 1} / {totalPages})
                            </div>

                            <div className="flex items-center gap-1.5">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="rounded-lg h-9 px-3 text-xs"
                                  disabled={page === 0 || loading}
                                  onClick={() => handlePageChange(page - 1)}
                                >
                                    이전
                                </Button>

                                <div className="flex items-center gap-1">
                                    {Array.from({ length: totalPages }, (_, i) => i).map((p) => {
                                        // 💡 페이지가 많을 경우 ... 으로 생략하는 로직
                                        if (
                                          totalPages > 7 &&
                                          Math.abs(p - page) > 2 &&
                                          p !== 0 &&
                                          p !== totalPages - 1
                                        ) {
                                            if (p === 1 || p === totalPages - 2) {
                                                return (
                                                  <span key={p} className="px-1 text-xs text-slate-400">
                                                            ...
                                                        </span>
                                                )
                                            }
                                            return null
                                        }

                                        return (
                                          <Button
                                            key={p}
                                            variant={p === page ? "default" : "ghost"}
                                            size="sm"
                                            className={`h-8 w-8 rounded-lg p-0 text-xs font-medium ${
                                              p === page
                                                ? "bg-slate-900 text-white hover:bg-slate-800"
                                                : "text-slate-600 hover:bg-slate-100"
                                            }`}
                                            onClick={() => handlePageChange(p)}
                                            disabled={loading}
                                          >
                                              {p + 1}
                                          </Button>
                                        )
                                    })}
                                </div>

                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="rounded-lg h-9 px-3 text-xs"
                                  disabled={page >= totalPages - 1 || loading}
                                  onClick={() => handlePageChange(page + 1)}
                                >
                                    다음
                                </Button>
                            </div>
                        </div>
                      )}
                  </CardContent>
              </Card>
          </div>
      </AdminLayout>
    )
}